import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { QuiIcon } from "../_primitives/QuiIcon/QuiIcon.js";
import { createPortal } from "react-dom";
const DEFAULT_TOAST_TIMEOUT_IN_MILLISECONDS = 5000;
function randomId() {
    const t = new Uint8Array(12);
    const r = window.crypto.getRandomValues(t);
    const numStrings = [];
    r.forEach((n) => {
        numStrings.push(n.toString(16).padStart(2, "0"));
    });
    return numStrings.join("");
}
function useToastsInternal() {
    const [toasts, setToasts] = useState([]);
    const displayToasts = useMemo(() => {
        return [...toasts].sort((a, b) => {
            return b.addedTimestamp - a.addedTimestamp;
        });
    }, [toasts]);
    const addToast = useCallback(({ title, timeout, variant }) => {
        const toastTimeout = typeof timeout === "number" && timeout > 0 ? timeout : DEFAULT_TOAST_TIMEOUT_IN_MILLISECONDS;
        const addedTimestamp = new Date().valueOf();
        const expirationTimestamp = addedTimestamp + toastTimeout;
        const id = randomId();
        const newToast = { title, variant, id, expirationTimestamp, addedTimestamp };
        setToasts((currentToasts) => [...currentToasts, newToast]);
    }, []);
    const dismissToast = useCallback((toastId) => {
        setToasts((currentToasts) => {
            return currentToasts.filter((t) => t.id !== toastId);
        });
    }, []);
    useEffect(() => {
        if (toasts.length === 0)
            return;
        const nextExpirationTimestamp = [...toasts]
            .map((t) => t.expirationTimestamp)
            .sort((a, b) => a - b)
            .shift();
        if (nextExpirationTimestamp === undefined)
            return;
        const now = new Date().valueOf();
        const timeTillNextTimeout = nextExpirationTimestamp - now;
        if (timeTillNextTimeout < 0) {
            setToasts((currentToasts) => {
                const now = new Date().valueOf();
                return currentToasts.filter((t) => t.expirationTimestamp > now);
            });
            return;
        }
        const timeout = setTimeout(() => {
            setToasts((currentToasts) => {
                const now = new Date().valueOf();
                return currentToasts.filter((t) => t.expirationTimestamp > now);
            });
        }, timeTillNextTimeout);
        return () => {
            clearTimeout(timeout);
        };
    }, [toasts]);
    return {
        toasts: displayToasts,
        addToast,
        dismissToast,
    };
}
/** A toast component. Note: Toasts can be triggered within the QuiToastProvider subtree using the useAddToast hook */
export function QuiToast({ id, title, variant }) {
    const { dismissToast } = React.useContext(ToastContext);
    const className = classNames("qui-toast", {
        [`qui-toast-variant-${variant}`]: variant,
    });
    return (React.createElement("div", { className: className },
        React.createElement("div", null, title),
        React.createElement("button", { type: "button", onClick: () => {
                dismissToast(id);
            } },
            React.createElement(QuiIcon, { icon: "x" }))));
}
const ToastContext = React.createContext({});
export function useQuiToasts() {
    return useContext(ToastContext).addToast;
}
export function QuiToastsProvider({ children }) {
    const { toasts, addToast, dismissToast } = useToastsInternal();
    const contextValue = useMemo(() => ({
        addToast,
        dismissToast,
    }), [dismissToast, addToast]);
    return (React.createElement(ToastContext.Provider, { value: contextValue },
        children,
        createPortal(React.createElement("div", { className: "qui-toasts" }, toasts.map(({ id, title, variant }) => (React.createElement(QuiToast, { key: id, variant: variant, title: title, id: id })))), document.body)));
}
